import record from "@/assets/Bell.mp3";
import NoOrder from "@/assets/images/bell (1) 1.png";
import { useFetchBusinessSetting } from "@/components/hooks/useFetchBusinessSetting";
import { FloorType, productStatus, ReceiveChefType, TableType } from "@/config";
import { SocketEvent } from "@/enum/socketEvent";
import { apiGetFloors } from "@/services/ServiceApi";
import { useAppContext } from "@/use-context/Context";
import { getShop } from "@/utils/auth";
import socket from "@/utils/socket";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { FaCircleCheck } from "react-icons/fa6";
import { HiExclamationCircle } from "react-icons/hi";
import { showToast } from "../OrderByPOS/Component/Message/ShowToast";
import { handleSocketStatusProduct } from "../OrderByPOS/Component/Message/handleSocketStatusProduct";
import ListTable from "./Component/ListTable";
import StatusBar from "./Component/StatusBar";
import StatusBarMobile from "./Component/StatusBarMobile";
import TopBar from "@/components/Themes/Rubick/TopBar";

export default function Main() {
  useFetchBusinessSetting();
  const shop = getShop();
  const { t } = useTranslation();
  const sound = new Audio(record);
  const { setAddToCart } = useAppContext();

  const [loading, setLoading] = useState<boolean>(false);
  const [floorData, setFloorData] = useState<FloorType[]>([]);

  const fetchFloor = (id: string) => {
    setLoading(true);
    apiGetFloors(id)
      .then((response) => {
        if (response.success) {
          const result = response.data;
          setFloorData(result);
          setTimeout(() => {
            setLoading(false);
          }, 1000);
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (shop) {
      fetchFloor(shop._id);
    }
  }, []);

  const updateTableById = (
    floors: FloorType[],
    tableId: string,
    updater: (table: TableType) => TableType
  ) => {
    return floors.map((floor) => ({
      ...floor,
      tables: floor.tables.map((table) =>
        table._id === tableId ? updater(table) : table
      )
    }));
  };

  const handleSocketEventCustomer = (
    event: string,
    updateKey: string,
    message: string,
    sound: any
  ) => {
    socket.on(event, (data) => {
      if (data.success) {
        sound?.play?.()?.catch?.(() => {});
        fetchFloor(shop ? shop._id : "");
        const tableId = data.data.tableId;
        const { tableName } = data.data;

        setFloorData((prev) =>
          updateTableById(prev, tableId, (table) => ({
            ...table,
            colorStatus: {
              ...table.colorStatus,
              [updateKey]: true
            }
          }))
        );

        showToast(
          message,
          tableName,
          <HiExclamationCircle size={24} style={{ color: "#527FDF" }} />,
          { reverse: false }
        );
      }
    });

    return () => {
      socket.off(event);
    };
  };

  const handleSocketEvent = (
    event: string,
    updateKey: string,
    message: string,
    sound: any
  ) => {
    socket.on(event, (data) => {
      if (data?.success && data?.data) {
        fetchFloor(shop ? shop._id : "");
        const items = Array.isArray(data.data) ? data.data : [data.data];
        items.forEach((item: any) => {
          sound?.play?.()?.catch?.(() => {});

          setFloorData((prev) =>
            updateTableById(prev, item.tableId, (table) => ({
              ...table,
              colorStatus: {
                ...table.colorStatus,
                [updateKey]: true
              }
            }))
          );

          showToast(
            message,
            item.tableName,
            <HiExclamationCircle size={24} style={{ color: "#527FDF" }} />,
            { reverse: false }
          );
        });
      }
    });

    return () => {
      socket.off(event);
    };
  };

  const handleSocketEventChef = (
    event: string,
    updateKey: string,
    updateKeyNew: string,
    type: boolean,
    sound: any,
    isAdmin: boolean
  ) => {
    socket.on(event, (data: ReceiveChefType) => {
      if (data.success) {
        fetchFloor(shop ? shop._id : "");
        sound?.play?.()?.catch?.(() => {});
        const status = data.status === productStatus.Ready;

        setFloorData((prev) =>
          prev.map((floor) => ({
            ...floor,
            tables: floor.tables.map((table) => {
              const match = data.data.some((i) => i.tableId === table._id);
              if (!match) return table;

              return {
                ...table,
                colorStatus: {
                  ...table.colorStatus,
                  [updateKey]: status,
                  ...(updateKeyNew && { [updateKeyNew]: data.available })
                }
              };
            })
          }))
        );

        if (isAdmin && data?.data) {
          const items = Array.isArray(data.data) ? data.data : [data.data];
          items.forEach((item: any) => {
            const message = t("cookingReady");
            const icon = (
              <FaCircleCheck size={24} style={{ color: "#52DF77" }} />
            );
            showToast(message, item.name, icon, { reverse: true }, item.nameKH);
          });
        }
      }
    });

    return () => {
      socket.off(event);
    };
  };

  useEffect(() => {
    setAddToCart([]);
  }, []);

  useEffect(() => {
    const handleOrderReceived = (response: any) => {
      if (response) {
        fetchFloor(shop ? shop._id : "");
      }
    };

    socket.on(
      SocketEvent.WAITER_RECEIVE_STATUS_TRACKING_ORDER,
      handleOrderReceived
    );
    socket.on(SocketEvent.ACCEPT_ORDER, handleOrderReceived);
    socket.on(SocketEvent.COOK_ORDER, handleOrderReceived);
    socket.on(SocketEvent.REJECT_ORDER, handleOrderReceived);

    return () => {
      socket.off(
        SocketEvent.WAITER_RECEIVE_STATUS_TRACKING_ORDER,
        handleOrderReceived
      );
      socket.off(SocketEvent.ACCEPT_ORDER, handleOrderReceived);
      socket.off(SocketEvent.COOK_ORDER, handleOrderReceived);
      socket.off(SocketEvent.REJECT_ORDER, handleOrderReceived);
    };
  }, [socket]);

  useEffect(
    () =>
      handleSocketEvent(
        SocketEvent.WAITER_RECEIVE_PAYMENT,
        "available",
        t("paymentCompletedAtTable"),
        sound
      ),
    []
  );
  useEffect(
    () =>
      handleSocketEventCustomer(
        SocketEvent.WAITER_SUPPORT_CUSTOMER,
        "support",
        t("customerNeedsSupportAtTable"),
        sound
      ),
    []
  );
  useEffect(
    () =>
      handleSocketEventCustomer(
        SocketEvent.WAITER_RECEIVE_ORDER_FROM_CUSTOMER,
        "newOrder",
        t("youHaveANewOrderAtTable"),
        sound
      ),
    []
  );
  useEffect(
    () =>
      handleSocketEventCustomer(
        SocketEvent.FREE_ITEM_REQUEST,
        "newOrder",
        t("youHaveANewOrderAtTable"),
        sound
      ),
    []
  );

  // useEffect(() => handleSocketEventChef(SocketEvent.WAITER_RECEIVE_TABLE_COLORS_STATUS_TRACKING, "chef", "available", true, sound, false), []);
  // useEffect(() => handleSocketEventChef(SocketEvent.WAITER_RECEIVE_CONFIRM_ORDER, "chef", "", false, sound, false), []);
  // useEffect(() => handleSocketStatusProduct(SocketEvent.WAITER_RECEIVE_STATUS_TRACKING_ORDER, sound, false), []);

  useEffect(
    () => handleSocketStatusProduct(SocketEvent.ACCEPT_ORDER, sound, false),
    []
  );
  useEffect(
    () => handleSocketStatusProduct(SocketEvent.REJECT_ORDER, sound, false),
    []
  );
  useEffect(
    () => handleSocketStatusProduct(SocketEvent.COOK_ORDER, sound, false),
    []
  );
  useEffect(
    () =>
      handleSocketEventChef(
        SocketEvent.READy_ORDER,
        "chef",
        "available",
        true,
        sound,
        true
      ),
    []
  );
  useEffect(
    () =>
      handleSocketEventChef(
        SocketEvent.DONE_ORDER,
        "chef",
        "",
        false,
        sound,
        false
      ),
    []
  );

  const {
    availableCount,
    unavailableCount,
    requestOrderCount,
    supportCounts,
    newOrderCounts,
    chefCounts
  } = floorData.reduce(
    (acc, floor) => {
      floor.tables.forEach((table) => {
        if (table.colorStatus?.available) acc.availableCount++;
        else acc.unavailableCount++;

        if (table.colorStatus?.requestOrder) acc.requestOrderCount++;
        if (table.colorStatus?.support) acc.supportCounts++;
        if (table.colorStatus?.newOrder) acc.newOrderCounts++;
        if (table.colorStatus?.chef) acc.chefCounts++;
      });
      return acc;
    },
    {
      availableCount: 0,
      unavailableCount: 0,
      requestOrderCount: 0,
      supportCounts: 0,
      newOrderCounts: 0,
      chefCounts: 0
    }
  );

  return (
    <div className="container h-screen overflow-auto pb-[20px] 2xl:px-24">
      <TopBar />
      <StatusBar
        availableCount={availableCount}
        unavailableCount={unavailableCount}
        requestOrderCount={requestOrderCount}
        supportCounts={supportCounts}
        newOrderCounts={newOrderCounts}
        chefCounts={chefCounts}
      />
      <StatusBarMobile
        availableCount={availableCount}
        unavailableCount={unavailableCount}
        requestOrderCount={requestOrderCount}
        supportCounts={supportCounts}
        newOrderCounts={newOrderCounts}
        chefCounts={chefCounts}
      />
      {loading ? (
        <div className="h-[80vh] flex items-center justify-center">
          <AiOutlineLoading3Quarters
            size={32}
            className="animate-spin inline-block text-[#767676]"
          />
        </div>
      ) : floorData.length ? (
        <div className="pl-4 pr-1.5 md:p-8 overflow-y-scroll overflow-x-hidden">
          <div className="pb-[60px]">
            {floorData &&
              floorData.map((floor: FloorType, index: number) => {
                const isDeliveryFloor = floor.tables?.some(
                  (table) => table.type.toLowerCase() === "delivery"
                );
                return (
                  <div key={index}>
                    <h1 className="text-[18px] 3xl:text-sm font-bold line-clamp-1 text-[#475569] py-4">
                      {isDeliveryFloor ? t("delivery") : floor.name}
                    </h1>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-9 gap-4 rounded-lg">
                      {floor.tables &&
                        floor.tables.map((table: TableType, index: number) => (
                          <ListTable key={index} table={table} />
                        ))}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      ) : (
        <div className="h-[40vh] flex items-center justify-center">
          <div className="flex flex-col items-center">
            <div className="min-h-[80] min-w-[80] max-w-[80]">
              <img
                src={NoOrder}
                className="rounded-[10px] object-cover h-[80] w-[80px]"
                alt="Asian Food"
              />
            </div>
            <p className="text-[16px] font-medium text-[#767676] ">
              {t("noTable")}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
